import React, { useContext } from 'react';
import { SystemSettingsContext } from '../../../contexts/SystemSettingsContext';
import { breakdownMainCellStyle, campaignBreakdownTableStyle, panelTitleStyle } from './BespokeBillingStyles';
import { sumAllBillingPlanLines } from './BespokeBillingUtils';
import { BusinessTypes, CampaignGoalEnum, DealTypes } from '../../../constants/Enums';

const CampaignBreakdown = ({
    campaign,
    calcVatRate,
    invoices,
    sumAllPropertiesLines,
    isSAndP,
    offairTotal,
    currentDeal,
    selectedGoal,
    offairActivitiesForBilling,
    sandPBudgetDetails
}) => {
    const { formatCurrency } = useContext(SystemSettingsContext);

    let campaignTotal = 0;
    let commissionRate = 0;
    let campaignCommission = 0;
    let campaignTotalMinusCommission = 0;
    let vatTotal = 0;
    let billingAmount = 0;
    let valuePotSpend = 0;
    const localCampaign = campaign;
    if (
        localCampaign.campaignStatusName === 'Booked' ||
        localCampaign.campaignStatusName === 'Running' ||
        localCampaign.campaignStatusName === 'Completed'
    ) {
        campaignTotal = parseFloat(campaign?.budget);
        if (isSAndP) {
            campaignTotal += parseFloat(sumAllPropertiesLines('sandPBudget'));
        }

        campaignTotal -=
            isSAndP || (currentDeal?.type === DealTypes.AGENCY && !!currentDeal?.maxValuePot)
                ? campaign?.valuePotSpend
                : 0;

        commissionRate = !campaign.barterAgencyAccountId ? campaign.commissionRate : campaign.barterCommissionRate;
        campaignCommission = (campaignTotal * commissionRate) / 100 + offairTotal?.commissionTotal;
        campaignTotalMinusCommission = campaignTotal + offairTotal?.invoiceTotal - campaignCommission;
        vatTotal = (calcVatRate(campaign.vatId) * campaignTotalMinusCommission) / 100;
        billingAmount = campaignTotalMinusCommission + vatTotal;
        valuePotSpend = campaign?.valuePotSpend;
    } else {
        // sandPBudgetDetails to display last booked campaign's budget
        if (sandPBudgetDetails !== undefined) {
            if (localCampaign?.businessType?.toLowerCase() === BusinessTypes.AIRTIME.name.toLocaleLowerCase()) {
                campaignTotal = parseFloat(sandPBudgetDetails?.airtimeBudget);
            }
            if (isSAndP) {
                campaignTotal = parseFloat(sandPBudgetDetails?.budget) + parseFloat(sandPBudgetDetails?.sandPBudget);
            }

            campaignTotal -=
                isSAndP ||
                (sandPBudgetDetails?.type === DealTypes.AGENCY &&
                    !!sandPBudgetDetails?.maxValuePot &&
                    sandPBudgetDetails.campaignGoal.goal === CampaignGoalEnum.BUDGET)
                    ? sandPBudgetDetails?.valuePotSpend
                    : 0;

            commissionRate = sandPBudgetDetails?.commissionRate;
            campaignCommission = (campaignTotal * commissionRate) / 100 + offairTotal?.commissionTotal;
            campaignTotalMinusCommission = campaignTotal + offairTotal?.invoiceTotal - campaignCommission;
            vatTotal = (calcVatRate(sandPBudgetDetails?.vatId) * campaignTotalMinusCommission) / 100;
            billingAmount = campaignTotalMinusCommission + vatTotal;
            valuePotSpend = sandPBudgetDetails?.valuePotSpend;
        }
    }

    let CommissionableAmount = 0;
    let NonCommissionableAmount = 0;
    commissionRate > 0 ? (CommissionableAmount += campaignTotal) : (NonCommissionableAmount += campaignTotal);

    if (offairActivitiesForBilling?.length > 0) {
        offairActivitiesForBilling.forEach((oa) => {
            if (oa.status === 3) {
                const amount = parseFloat(oa.price);
                oa.commission > 0 ? (CommissionableAmount += amount) : (NonCommissionableAmount += amount);
            }
        });
    }

    let billCommissionableAmount = 0;
    let billNonCommissionableAmount = 0;

    invoices?.forEach((i) => {
        i.billingPlanInvoiceLines?.forEach((il, i) => {
            const comm = parseFloat(il?.commissionAmount);
            const amount = il?.airtimeAmount + il?.offAirAmount;
            comm > 0 ? (billCommissionableAmount += amount) : (billNonCommissionableAmount += amount);
        });
    });

    return (
        <div style={{ width: 505, marginTop: 24 }}>
            <span style={panelTitleStyle}>{window.translate('Billing Validation')}</span>
            <table style={{ ...campaignBreakdownTableStyle, marginTop: 5 }}>
                <thead>
                    <tr>
                        <th style={{ ...breakdownMainCellStyle(0), width: 180, height: 40, textAlign: 'right' }}></th>
                        <th style={{ ...breakdownMainCellStyle(0), width: 130, height: 40 }}>Campaign Value</th>
                        <th style={{ ...breakdownMainCellStyle(0), width: 130, height: 40 }}>Invoiced Amount</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td align='right' style={{ ...breakdownMainCellStyle(1), width: 180, height: 30 }}>
                            {window.translate('Commissionable')}
                        </td>
                        <td
                            align='right'
                            style={{ ...breakdownMainCellStyle(1), width: 130, height: 30, fontWeight: 'normal' }}
                        >
                            {isNaN(CommissionableAmount) ? 0 : formatCurrency(CommissionableAmount)}
                        </td>
                        <td
                            align='right'
                            style={{ ...breakdownMainCellStyle(1), width: 130, height: 30, fontWeight: 'normal' }}
                        >
                            {isNaN(billCommissionableAmount) ? 0 : formatCurrency(billCommissionableAmount)}
                        </td>
                    </tr>
                    <tr>
                        <td align='right' style={{ ...breakdownMainCellStyle(1), width: 180, height: 30 }}>
                            {window.translate('Non Commissionable')}
                        </td>
                        <td
                            align='right'
                            style={{ ...breakdownMainCellStyle(1), width: 130, height: 30, fontWeight: 'normal' }}
                        >
                            {isNaN(NonCommissionableAmount) ? 0 : formatCurrency(NonCommissionableAmount)}
                        </td>
                        <td
                            align='right'
                            style={{ ...breakdownMainCellStyle(1), width: 130, height: 30, fontWeight: 'normal' }}
                        >
                            {isNaN(billNonCommissionableAmount) ? 0 : formatCurrency(billNonCommissionableAmount)}
                        </td>
                    </tr>

                    {isSAndP ||
                    (currentDeal?.type === DealTypes.AGENCY &&
                        !!currentDeal?.maxValuePot &&
                        selectedGoal.goal === CampaignGoalEnum.BUDGET) ? (
                        <tr>
                            <td align='right' style={{ ...breakdownMainCellStyle(1), width: 180, height: 30 }}>
                                {window.translate('Value Pot Spend')}
                            </td>
                            <td
                                align='right'
                                style={{ ...breakdownMainCellStyle(1), width: 130, height: 30, fontWeight: 'normal' }}
                            >
                                {isNaN(valuePotSpend) ? 0 : formatCurrency(valuePotSpend)}
                            </td>
                            <td
                                align='right'
                                style={{ ...breakdownMainCellStyle(1), width: 130, height: 30, fontWeight: 'normal' }}
                            >
                                -
                            </td>
                        </tr>
                    ) : (
                        <></>
                    )}
                    <tr>
                        <td align='right' style={{ ...breakdownMainCellStyle(1), width: 180, height: 30 }}>
                            {window.translate('Less Commission')}
                        </td>
                        <td
                            align='right'
                            style={{ ...breakdownMainCellStyle(1), width: 130, height: 30, fontWeight: 'normal' }}
                        >
                            {isNaN(campaignCommission) ? 0 : formatCurrency(campaignCommission)}
                        </td>
                        <td
                            align='right'
                            style={{ ...breakdownMainCellStyle(1), width: 130, height: 30, fontWeight: 'normal' }}
                        >
                            {isNaN(sumAllBillingPlanLines(invoices, 'commissionAmount'))
                                ? 0
                                : formatCurrency(sumAllBillingPlanLines(invoices, 'commissionAmount'))}
                        </td>
                    </tr>
                    <tr>
                        <td align='right' style={{ ...breakdownMainCellStyle(0), width: 180, height: 30 }}>
                            {window.translate('Total (Net of VAT)')}
                        </td>
                        <td
                            align='right'
                            style={{ ...breakdownMainCellStyle(0), width: 130, height: 30, fontWeight: 'normal' }}
                        >
                            {isNaN(campaignTotalMinusCommission) ? 0 : formatCurrency(campaignTotalMinusCommission)}
                        </td>
                        <td
                            align='right'
                            style={{ ...breakdownMainCellStyle(0), width: 130, height: 30, fontWeight: 'normal' }}
                        >
                            {isNaN(sumAllBillingPlanLines(invoices, 'netAmount'))
                                ? 0
                                : formatCurrency(sumAllBillingPlanLines(invoices, 'netAmount'))}
                        </td>
                    </tr>
                    <tr>
                        <td align='right' style={{ ...breakdownMainCellStyle(1), width: 180, height: 30 }}>
                            {window.translate('Total VAT')}
                        </td>
                        <td
                            align='right'
                            style={{ ...breakdownMainCellStyle(1), width: 130, height: 30, fontWeight: 'normal' }}
                        >
                            {isNaN(vatTotal) ? 0 : formatCurrency(vatTotal)}
                        </td>
                        <td
                            align='right'
                            style={{ ...breakdownMainCellStyle(1), width: 130, height: 30, fontWeight: 'normal' }}
                        >
                            {isNaN(sumAllBillingPlanLines(invoices, 'vatAmount'))
                                ? 0
                                : formatCurrency(sumAllBillingPlanLines(invoices, 'vatAmount'))}
                        </td>
                    </tr>
                    <tr>
                        <td align='right' style={{ ...breakdownMainCellStyle(0), width: 180, height: 30 }}>
                            {window.translate('Total Billing Amounts')}
                        </td>
                        <td align='right' style={{ ...breakdownMainCellStyle(0), width: 130, height: 30 }}>
                            {isNaN(billingAmount) ? 0 : formatCurrency(billingAmount)}
                        </td>
                        <td align='right' style={{ ...breakdownMainCellStyle(0), width: 130, height: 30 }}>
                            {isNaN(sumAllBillingPlanLines(invoices, 'grossAmount'))
                                ? 0
                                : formatCurrency(sumAllBillingPlanLines(invoices, 'grossAmount'))}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

export default CampaignBreakdown;
